import React from "react";
import { Box, Flex, Button } from "@chakra-ui/react";
import { IntercomProvider, useIntercom } from "react-use-intercom";
import * as Sentry from "@sentry/react";

// Layout components
import WithQuery from "../components/WithQuery";
import { CURRENT_USER_QUERY } from "../graphql/users";
import { Logout } from "../services/MoblyzeAuth";
import MoblyzeLocalStorage, {
  LOCAL_STORAGE_KEYS,
} from "../services/MoblyzeLocalStorage";
import { HasAnalytics } from "../services/MoblyzeAuth";

import Sidebar from "../components/Sidebar";
import FeatureFlagsPanel from "../components/FeatureFlagsPanel";
import { setFeatureFlagUserContext } from "../services/featureFlags";
import {
  INTERCOM_APP_ID_JAB,
  INTERCOM_APP_ID_MOBLYZE,
} from "../helpers/config";
const CandididateLayout = () => {
  const { shutdown } = useIntercom();
  const onLogoutPress = () => {
    // Logout from Intercom library.
    shutdown();
    Logout();
  };

  return (
    <Flex as="section" minH="100vh" flexDir="column">
      <Flex as="section">
        <Box flex={1} width={"100%"}>
          <Box
            p="6"
            bg="red.500"
            color="white"
            fontWeight="bold"
            borderRadius="md"
          >
            You are not authorized to access this page.
          </Box>
        </Box>
      </Flex>

      <Button mt={10} onClick={onLogoutPress} colorScheme="red">
        Sign Out
      </Button>
    </Flex>
  );
};

const BaseLayout = ({
  children,
  showSidebar,
  showGradientBackground,
  currentUser,
  sendbirdNotifications,
  showCountLoader
}) => {
  return (
    <Flex as="section" minH="100vh" flexDir="column">
      <Flex as="section" flex="1" overflowX="scroll" flexDir="row">
        {showSidebar &&
          <Sidebar currentUser={currentUser} sendbirdNotifications={sendbirdNotifications} showCountLoader={showCountLoader} />
        }
        <Box
          flex={1}
          px="4"
          py="4"
          position="relative"
          minW="0"
          bg={
            showGradientBackground
              ? "linear-gradient(180deg, rgba(41, 118, 188, 0.20) 6.69%, rgba(0, 0, 0, 0.00) 45.12%), #1D1D1D"
              : "var(--greyDark), #1A202C;"
          }
        >
          {children}
        </Box>
      </Flex>
    </Flex>
  );
};

function AppLayoutInner(props) {
  const { boot } = useIntercom();
  const {
    children,
    data,
    loading,
    refetch,
    showGradientBackground,
    initializeIntercom,
  } = props;

  const [userRole, setUserRole] = React.useState(data?.currentUser?.role);
  const [showCountLoader, setShowCountLoader] = React.useState(true);
  const [sendbirdNotifications, setSendbirdNotifications] = React.useState([]);

  React.useEffect(() => {
    if (!data?.currentUser?.id) {
        setSendbirdNotifications([]);
        return;
    }

    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const token = currentUser?.token;

    if (!token) {
        setSendbirdNotifications([]);
        return;
    }

    const eventSourceUrl = `${process.env.REACT_APP_API_URL}/sendbird/notification/list?user_id=${data.currentUser.id}&token=${token}`;
    const events = new EventSource(eventSourceUrl);

    events.onmessage = (event) => {
        try {
            const parsedData = JSON.parse(event.data);
            setSendbirdNotifications(parsedData?.data || []);
        } catch (error) {
            console.error("Error parsing SSE data:", error, "Raw data:", event.data);
        } finally {
            setShowCountLoader(false);
        }
    };

    events.onerror = (error) => {
        events.close();
        setShowCountLoader(false);
    };

    return () => {
        events.close();
    };
}, [data?.currentUser?.id]);

  React.useEffect(() => {
    if (data?.currentUser?.role && initializeIntercom) {
      setUserRole(data?.currentUser?.role);
      // Boot the Intercom library with the user's data.
      const name = `${data?.currentUser?.firstName} ${data?.currentUser?.lastName}`;
      const userId = data?.currentUser?.id;
      const email = data?.currentUser?.email;
      const role = data?.currentUser?.role;

      boot({
        userId,
        name: name,
        email: email,
        customAttributes: { role },
      });

      const setAnalyticsIdentify = async () => {
        const hasAnalyticsValue = await HasAnalytics();
        if (!hasAnalyticsValue) {
          // notify segment of logged in user
          window.analytics.identify(userId, {
            name: name,
            email: email,
            role: role,
          });
          await MoblyzeLocalStorage.save({
            key: LOCAL_STORAGE_KEYS.ANALYTICS,
            data: true,
          });
        }
      };
      setAnalyticsIdentify();

      // identify on sentry
      Sentry.setUser({
        id: userId,
        email: email,
      });

      window.analytics.page();
    }
  }, [data?.currentUser, data?.currentUser?.role, boot, initializeIntercom]);

  const currentUser = data?.currentUser;

  // Push the signed-in user's role, agency and id to Remote Config as custom
  // signals so console conditions can target feature flags on them. Owner
  // users are surfaced as the "Admin" role — we don't expose a separate owner
  // signal.
  const role = currentUser?.isOwner ? "Admin" : currentUser?.role;
  const agencySlug =
    currentUser?.recruiterAgencySlugId ||
    currentUser?.agency?.slugId ||
    currentUser?.clientOrganization?.agencySlugId;
  const userId = currentUser?.id;

  React.useEffect(() => {
    if (!role) return;
    setFeatureFlagUserContext({ role, agencySlug, userId });
  }, [role, agencySlug, userId]);

  // Display error message if a candidate happens to access this app
  if (userRole === "Candidate") {
    return <CandididateLayout />;
  }

  return (
    <BaseLayout
      showSidebar={userRole === "Recruiter"}
      showGradientBackground={showGradientBackground}
      showCountLoader={showCountLoader}
      currentUser={currentUser}
      sendbirdNotifications={sendbirdNotifications}
    >
      {/* embed current user prop on children, along with loading and refetch */}
      {React.cloneElement(children, { loading, currentUser, refetch })}
      <FeatureFlagsPanel />
    </BaseLayout>
  );
}

function AppLayoutIntercomWrapper(props) {
  const { data } = props;

  const isJabAgency =
    data?.currentUser?.clientOrganization?.agencySlugId === "JAB" ||
    data?.currentUser?.recruiterAgencySlugId === "JAB";

  const initializeIntercom =
    data?.currentUser?.clientOrganization?.agencySlugId ||
    data?.currentUser?.recruiterAgencySlugId;

  return (
    <IntercomProvider
      appId={isJabAgency ? INTERCOM_APP_ID_JAB : INTERCOM_APP_ID_MOBLYZE}
      shouldInitialize={initializeIntercom}
    >
      <AppLayoutInner {...props} initializeIntercom={initializeIntercom} />
    </IntercomProvider>
  );
}

export default function AppLayout({ showGradientBackground, children }) {
  return WithQuery(AppLayoutIntercomWrapper, CURRENT_USER_QUERY, null, {
    children,
    showGradientBackground,
  })();
}

export const PlainLayout = ({
  children,
}) => {
  return (
    <Flex as="section" minH="100vh" flexDir="column">
      <Flex as="section" flex="1" overflowX="scroll" flexDir="row">
        <Box
          flex={1}
          py={{base: 5, sm: 10}}
          px={{base: 4, sm: 8}}
          position="relative"
          bg={ "var(--greyDark), #1A202C;" }
        >
          {children}
        </Box>
      </Flex>
    </Flex>
  );
};
